import { useState, useEffect } from 'react'

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
  prompt(): Promise<void>
}

export function usePWA() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [canInstall, setCanInstall] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)
  const [updateAvailable, setUpdateAvailable] = useState(false)

  useEffect(() => {
    // Detectar si ya está instalada (modo standalone)
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as any).standalone === true

    if (isStandalone) {
      setIsInstalled(true)
      return
    }

    // Capturar el prompt de instalación del navegador
    const handler = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      setCanInstall(true)
    }

    window.addEventListener('beforeinstallprompt', handler)

    // Detectar cuando se instala exitosamente
    const installedHandler = () => {
      setIsInstalled(true)
      setCanInstall(false)
      setDeferredPrompt(null)
    }
    window.addEventListener('appinstalled', installedHandler)

    return () => {
      window.removeEventListener('beforeinstallprompt', handler)
      window.removeEventListener('appinstalled', installedHandler)
    }
  }, [])

  // Detectar nueva versión del Service Worker
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return

    const handleUpdate = () => setUpdateAvailable(true)

    navigator.serviceWorker.addEventListener('controllerchange', handleUpdate)

    // Workbox: escuchar mensaje de "nueva versión disponible"
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'SKIP_WAITING') setUpdateAvailable(true)
    })

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleUpdate)
    }
  }, [])

  const installPWA = async () => {
    if (!deferredPrompt) return false
    await deferredPrompt.prompt()
    const result = await deferredPrompt.userChoice
    if (result.outcome === 'accepted') {
      setCanInstall(false)
      setDeferredPrompt(null)
      return true
    }
    return false
  }

  const reloadForUpdate = () => {
    window.location.reload()
  }

  return { canInstall, isInstalled, updateAvailable, installPWA, reloadForUpdate }
}
